import { mapState, mapGetters } from 'vuex';
import { loadAgentChatScript } from '@services/officialTopupNew/payAssistant/loadSdk';
import {
  initPayAssistant,
  updatePayAssistant,
  notifyRightsConfig,
  destroyPayAssistant,
  isPayAssistantInitAborted,
} from '@services/officialTopupNew/payAssistant/instance';
import {
  startAntiHarassment,
  stopAntiHarassment,
  findExpiringCoupon,
} from '@services/officialTopupNew/payAssistant/antiHarassment';
import { notifyShoppingInteract } from '@services/officialTopupNew/payAssistant/shoppingInteract';
import { consumeCouponApplyPending } from '@services/officialTopupNew/payAssistant/couponTip';
import { sdkOpenLink } from '@utiles/officialStore/sdkCtrl';
import { getGlobalGetGotoLink } from '@utiles/globalGetLink';
import { LOAD_GAME_COUPON_LIST } from '@store/officialTopupNew/coupon';
import {
  UPDATE_IS_JP_UID_LOGIN,
  UPDATE_IS_LOGIN_POPUP,
} from '@store/officialTopupNew/login';
import { UPDATE_PAY_ASSISTANT } from '@store/officialTopupNew/page';

/**
 * 使用方式：
 * - 页面（如 Home）混入本 mixin
 * - 在合适时机（如 isFinishInit）手动调用 setupPayAssistant()
 * - 未开启（payAssistant.enable=false）时不会加载 SDK 脚本
 *
 * 附带能力：
 * - 初始化成功后自动启动「防骚扰」调度（券到期提醒 + 浏览沉默问询）
 * - 初始化时向 payAssistant 注入刷新/读取优惠券方法，供 REQUEST_COUPON 回调
 * - 注入 onOpenUrl，供 Chat App 内联系客服等 OPEN_URL 事件打开宿主页面
 * - 监听权益中心链接变化，同步给 AI 助手
 */
export default {
  data() {
    return {
      /** 是否正在初始化（防并发重复 setup） */
      payAssistantSetuping: false,
      /** 是否已初始化成功 */
      payAssistantReady: false,
      /** 已同步给助手的权益链接，避免重复通知 */
      notifiedRightsLink: '',
    };
  },
  computed: {
    ...mapState({
      payAssistant: (state) => state.page.payAssistant,
      rightsLink: (state) => state.page.rightsLink,
      // 防骚扰：券检测数据源
      gameCouponEnableList: (state) => state.coupon.gameCouponEnableList,
      // 防骚扰：进入支付确认面板视为已提单交互
      hasLoadPaytype: (state) => state.hasLoadPaytype,
      // 打开助手前登录守卫
      isLogin: (state) => state.login.isLogin,
      loginAccount: (state) => state.login.loginAccount,
      lang: (state) => state.lang,
      isLandscape: (state) => state.page.isLandscape,
    }),
    ...mapGetters(['isPayAssistantEnabled']),
  },
  watch: {
    /**
     * hasLoadPaytype=true 表示已进入确认面板（提单流程启动）
     * 此时应取消「浏览沉默问询」倒计时，避免打断选购
     */
    hasLoadPaytype(val) {
      if (val && this.payAssistantReady) {
        notifyShoppingInteract();
      }
    },
    /**
     * 权益链接变化且助手已就绪时同步给 AI
     * 助手后就绪时由 payAssistantReady watcher 补发
     */
    rightsLink(val) {
      this.syncRightsConfigToAssistant(val);
    },
    payAssistantReady(val) {
      if (val) {
        this.syncRightsConfigToAssistant(this.rightsLink);
      }
    },
    /**
     * 语言切换后通知 Chat App（SDK_CONFIG.lang），不改 chatAppUrl 避免 iframe remount
     * 【兜底】更新失败不影响页面语言切换本身
     */
    lang(lang) {
      if (!this.payAssistantReady || !lang) return;
      try {
        updatePayAssistant({ config: { lang } });
      } catch (err) {
        console.error('[PayAssistant] update lang failed', err);
      }
    },
    /**
     * 横竖屏切换时更新悬浮按钮位置与浮球拖拽开关
     */
    isLandscape() {
      this.$store.commit(UPDATE_PAY_ASSISTANT);
      if (!this.payAssistantReady) return;
      try {
        updatePayAssistant({
          config: {
            theme: this.payAssistant.theme,
            useFloatDrag: this.payAssistant.useFloatDrag,
          },
        });
      } catch (err) {
        console.error('[PayAssistant] update landscape config failed', err);
      }
    },
  },
  beforeDestroy() {
    /**
     * 【兜底】页面销毁：停防骚扰 + 销毁 SDK 壳/Observer
     * 避免对话遮罩或 MutationObserver 残留，导致下一页点不了或卡顿
     * （Home↔Result 会 destroy 后由新页 setup 重建，可接受）
     */
    if (this.payAssistantReady) {
      stopAntiHarassment();
    }
    consumeCouponApplyPending();
    if (this.payAssistantReady || this.payAssistantSetuping) {
      void destroyPayAssistant();
    }
    this.payAssistantReady = false;
    this.payAssistantSetuping = false;
    this.notifiedRightsLink = '';
  },
  methods: {
    /**
     * 手动初始化支付助手
     * - 未开启：直接返回 false，不加载脚本
     * - 已就绪：直接返回 true
     * - 成功后启动防骚扰调度
     * @returns {Promise<boolean>} 是否初始化成功
     */
    async setupPayAssistant(configOverride = {}) {
      if (this.payAssistantReady) {
        return true;
      }
      if (this.payAssistantSetuping || !this.isPayAssistantEnabled) {
        return false;
      }
      this.payAssistantSetuping = true;
      try {
        await loadAgentChatScript();
        if (this._isDestroyed) return false;

        await initPayAssistant({
          config: {
            ...(this.payAssistant || {}),
            lang: this.lang,
            loginAccount: this.loginAccount,
            ...configOverride,
          },
          // 宿主注入能力：刷新券列表 / 读取可用券 / 打开对话前守卫
          hostApi: {
            refreshCouponList: () =>
              this.$store.dispatch(LOAD_GAME_COUPON_LIST),
            getCouponList: () =>
              this.$store.state.coupon.gameCouponEnableList || [],
            // useBeforeShow=true 时由 SDK 调用；未登录先弹登录并拦截弹窗
            onBeforeShow: () => this.handlePayAssistantBeforeShow(),
            // Chat App OPEN_URL（联系客服 #findServiceContact 等）
            onOpenUrl: (url) => this.handlePayAssistantOpenUrl(url),
          },
        });

        // init 完成时页面可能已销毁（快速切页），勿标记 ready 并清掉孤儿实例
        if (this._isDestroyed) {
          void destroyPayAssistant();
          return false;
        }

        this.payAssistantReady = true;
        // SDK 就绪后开始 3s/30s 防骚扰计时
        this.startPayAssistantAntiHarassment();
        return true;
      } catch (err) {
        if (isPayAssistantInitAborted(err)) {
          return false;
        }
        // 【兜底】初始化失败只禁用助手，不阻断 Home isFinishInit 后的充值主流程
        console.error('[PayAssistant] setup failed', err);
        return false;
      } finally {
        this.payAssistantSetuping = false;
      }
    },

    /**
     * 打开助手对话前守卫（SDK useBeforeShow + onBeforeShow）
     * @returns {boolean} true 允许弹窗；false 拦截
     */
    handlePayAssistantBeforeShow() {
      if (this.isLogin) {
        return true;
      }
      // 与商品区未登录交互一致：弹出登录框
      this.$store.commit(UPDATE_IS_JP_UID_LOGIN, false);
      this.$store.commit(UPDATE_IS_LOGIN_POPUP, true);
      return false;
    },

    /**
     * Chat App → 宿主 OPEN_URL：直接打开 Chat App 返回的链接
     * @param {string} url
     */
    handlePayAssistantOpenUrl(url) {
      if (!url) return;
      sdkOpenLink({ link: getGlobalGetGotoLink({ link: url }) });
    },

    /**
     * 启动防骚扰调度，并向调度层注入宿主能力：
     * - getExpiringCoupon：从 vuex 读取即将过期券
     */
    startPayAssistantAntiHarassment() {
      startAntiHarassment({
        getExpiringCoupon: () =>
          findExpiringCoupon(this.gameCouponEnableList),
      });
    },

    /**
     * 权益中心链接有值且变化时，通知 AI 助手
     * @param {string} rightsLink
     */
    syncRightsConfigToAssistant(rightsLink) {
      if (!this.payAssistantReady || !rightsLink) {
        return;
      }
      if (rightsLink === this.notifiedRightsLink) {
        return;
      }
      notifyRightsConfig({ rightsLink })
        .then((ok) => {
          if (ok) {
            this.notifiedRightsLink = rightsLink;
          }
        })
        .catch((err) => {
          console.error(
            '[PayAssistant] syncRightsConfigToAssistant failed',
            err,
          );
        });
    },
  },
};
